import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatKgShort, kgToPieces, parseQty, parseWeightKg } from '../../utils/productionKg';
import {
  LabelSizeMm,
  apiWeight,
  drawQrPreview,
  fetchLabelSize,
  qrDataUrlFor,
  sanitizeWeight,
  writeLabelAndPrint,
} from '../../utils/spoolLabel';

interface ProductionItemData {
  id: number;
  linia: string | null;
  tydzien: string | null;
  material: string | null;
  srednica: string | null;
  waga: string | null;
  szpula: string | null;
  kolor: string | null;
  barwnik: string | null;
  doz_procent: string | null;
  marka: string | null;
  nazwa_oryg: string | null;
  indeks_kat: string | null;
  do_produkcji: string | null;
}

interface SavedSpool {
  id: number;
  label_code: string | null;
  weight_kg: number;
}

interface PrintOutcome {
  printed: boolean;
  printer: string | null;
  error_code: string | null;
  message: string;
}

type PrintState =
  | { phase: 'form' }
  | { phase: 'sending' }
  | { phase: 'sent'; printer: string }
  | { phase: 'failed'; message: string };

const statusBox: React.CSSProperties = {
  border: '1px solid', borderRadius: 8, padding: '12px 14px', fontSize: 14, marginBottom: 4,
};

const actionButton: React.CSSProperties = {
  flex: '1 1 160px', padding: '10px 12px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
};

/** How long the "sent to printer" confirmation stays before the window closes. */
const SENT_CONFIRMATION_MS = 1500;

interface Props {
  item: ProductionItemData;
  /** Wpis w magazynie dużych gabarytów, do którego przypinana jest zmierzona waga szpuli. */
  warehouseItemId?: number | null;
  initialWeight?: string | number | null;
  /**
   * Weighed big spool (big-spool lines): the print saves the spool — its weight (kg) is added to
   * the clicked shift and the item goes to the rewinding station — and prints its own label.
   */
  spool?: {
    assignmentId: number;
    shift: 'zmiana_1' | 'zmiana_2';
    onPrinted: () => Promise<void> | void;
  } | null;
  onClose: () => void;
}

export default function QRCodeModal({ item, warehouseItemId, initialWeight, spool, onClose }: Props) {
  const { t } = useLanguage();
  // Labels print on the printer of the line the user is signed in on (assignment line as fallback).
  const signedInLineId = useAuth().selectedLine?.id ?? null;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  // Waga wpisywana ręcznie; docelowo ma ją uzupełniać waga automatyczna.
  const initialWeightText = initialWeight != null ? String(initialWeight).replace('.', ',') : '';
  const [weight, setWeight] = useState<string>(initialWeightText);
  const [savedWeight, setSavedWeight] = useState<string>(initialWeightText);
  // A spool is at least one piece: never lighter than the item's weight (column "Waga").
  const minWeightKg = parseWeightKg(item.waga);
  const [savingWeight, setSavingWeight] = useState(false);

  // Label size comes from the admin printer settings.
  const [labelSize, setLabelSize] = useState<LabelSizeMm | null>(null);
  useEffect(() => {
    fetchLabelSize().then(setLabelSize);
  }, []);

  const [finishItem, setFinishItem] = useState(false);
  // Kilograms already on the item's labels — null until loaded, the finish option waits for it.
  const spoolMode = !!spool;
  const [weighedKg, setWeighedKg] = useState<number | null>(null);
  useEffect(() => {
    if (!spoolMode) return;
    api.get(`/production-items/${item.id}/spools`)
      .then((res) => setWeighedKg((res.data.data ?? []).reduce(
        (sum: number, s: { weight_kg: number }) => sum + Number(s.weight_kg), 0,
      )))
      .catch(() => undefined);
  }, [item.id, spoolMode]);
  // The item can be finished only once its labels — this one included — reach "Do produkcji".
  const plannedPcs = parseQty(item.do_produkcji);
  const typedKg = parseFloat(apiWeight(weight));
  const piecesAfter = weighedKg === null
    ? null
    : kgToPieces(weighedKg + (Number.isFinite(typedKg) && typedKg > 0 ? typedKg : 0), minWeightKg);
  const canFinish = plannedPcs <= 0 || (piecesAfter !== null && piecesAfter >= plannedPcs);
  // Spool mode: the label goes to the line's network printer; the browser print is the fallback.
  const [saved, setSaved] = useState<SavedSpool | null>(null);
  const [printState, setPrintState] = useState<PrintState>({ phase: 'form' });
  const shiftLabel = spool ? (spool.shift === 'zmiana_1' ? t('table.z1') : t('table.z2')) : '';

  const canSaveWeight = warehouseItemId != null;
  const showWeight = canSaveWeight || !!spool;
  const weightDirty = weight.trim() !== savedWeight.trim();

  const saveWeight = async (): Promise<boolean> => {
    if (!canSaveWeight || !weightDirty) return true;
    setSavingWeight(true);
    try {
      const trimmed = weight.trim();
      await api.put(`/warehouse/large/${warehouseItemId}/weight`, {
        actual_weight: trimmed === '' ? null : apiWeight(trimmed),
      });
      setSavedWeight(trimmed);
      return true;
    } catch (error: any) {
      alert(error?.response?.data?.message || t('largeWarehouse.qrModalWeightError'));
      return false;
    } finally {
      setSavingWeight(false);
    }
  };

  useEffect(() => {
    // QR carries week, line and product index. `id` is kept so the large-warehouse
    // rewind scanner can still match the scanned code to the production item.
    const qrData = JSON.stringify({
      id: item.id,
      tydzien: item.tydzien,
      linia: item.linia,
      indeks: item.indeks_kat,
    });

    if (canvasRef.current) {
      drawQrPreview(canvasRef.current, qrData);
    }
    qrDataUrlFor(qrData).then(setQrDataUrl);
  }, [item]);

  /** The browser label of a saved spool (fallback when the network printer is missing or failed). */
  const browserPrintSpool = async (printWindow: Window, spoolData: SavedSpool, widthMm: number, heightMm: number) => {
    const code = spoolData.label_code ?? String(spoolData.id);
    // The spool's own QR carries only the spool number; the rest is printed next to it.
    const spoolQr = await qrDataUrlFor(code);
    writeLabelAndPrint(printWindow, {
      title: `Etykieta szpuli ${code}`,
      qrDataUrl: spoolQr,
      widthMm,
      heightMm,
      rows: [
        { label: 'Linia', value: item.linia },
        { label: 'Tydzień', value: item.tydzien },
        { label: 'Materiał', value: item.material },
        ...(item.barwnik ? [{ label: 'Barwnik', value: item.barwnik }] : []),
        { label: 'Waga szpuli', value: `${formatKgShort(spoolData.weight_kg)} kg` },
        { label: 'Indeks', value: item.indeks_kat },
        { label: 'Nr szpuli', value: code },
      ],
    });
  };

  const applyOutcome = (outcome: PrintOutcome) => {
    if (outcome.printed) {
      setPrintState({ phase: 'sent', printer: outcome.printer ?? '' });
      window.setTimeout(onClose, SENT_CONFIRMATION_MS);
    } else {
      setPrintState({ phase: 'failed', message: outcome.message });
    }
  };

  /** Saves the spool (weight → shift, item → rewinding station); the backend prints it on the line's printer. */
  const saveAndPrintSpool = async () => {
    if (!spool) return;
    const parsed = parseFloat(apiWeight(weight));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      alert(t('spoolLabel.invalidWeight'));
      return;
    }
    // Compared in grams: no floating-point drift. The backend checks the same.
    if (minWeightKg > 0 && Math.round(parsed * 1000) < Math.round(minWeightKg * 1000)) {
      alert(t('spoolLabel.weightBelowItem', { min: formatKgShort(minWeightKg) }));
      return;
    }
    setPrintState({ phase: 'sending' });
    try {
      const res = await api.post(`/production-items/${item.id}/spools`, {
        production_assignment_id: spool.assignmentId,
        weight_kg: apiWeight(weight),
        shift: spool.shift,
        finish: finishItem && canFinish,
        production_line_id: signedInLineId,
      });
      setSaved(res.data.data as SavedSpool);
      // The table refresh runs on its own, so its failure never looks like a failed print.
      Promise.resolve(spool.onPrinted()).catch(() => undefined);
      applyOutcome(res.data.print as PrintOutcome);
    } catch (error: any) {
      const data = error?.response?.data;
      const message = data?.errors?.weight_kg?.[0] ?? data?.errors?.shift?.[0] ?? data?.message ?? t('myProduction.unknownError');
      setPrintState({ phase: 'form' });
      alert(t('spoolLabel.saveError', { message }));
    }
  };

  const retryNetworkPrint = async () => {
    if (!saved) return;
    setPrintState({ phase: 'sending' });
    try {
      const res = await api.post(`/production-spools/${saved.id}/print`, { production_line_id: signedInLineId });
      applyOutcome(res.data.print as PrintOutcome);
    } catch (error: any) {
      setPrintState({ phase: 'failed', message: error?.response?.data?.message ?? t('myProduction.unknownError') });
    }
  };

  const fallbackBrowserPrint = () => {
    if (!saved || !labelSize) return;
    // Opened synchronously in the click — after an await the browser would block it as a popup.
    const printWindow = window.open('', '_blank', 'width=640,height=480');
    if (!printWindow) return;
    void browserPrintSpool(printWindow, saved, labelSize.width, labelSize.height).then(onClose);
  };

  const handlePrint = async () => {
    if (spool) {
      // Network path: no browser window — the label goes to the printer of the line.
      await saveAndPrintSpool();
      return;
    }
    if (!qrDataUrl || !labelSize) return;
    // Okno otwieramy synchronicznie, jeszcze w obsłudze kliknięcia — po await przeglądarka
    // uznałaby je za popup bez gestu użytkownika i zablokowała.
    const printWindow = window.open('', '_blank', 'width=640,height=480');
    if (!printWindow) return;
    // Waga trafia na etykietę, więc musi być zapisana zanim ją wydrukujemy.
    if (!(await saveWeight())) {
      printWindow.close();
      return;
    }
    const printedWeight = weight.trim();
    writeLabelAndPrint(printWindow, {
      title: `Etykieta QR ${item.indeks_kat ?? ''}`,
      qrDataUrl,
      widthMm: labelSize.width,
      heightMm: labelSize.height,
      rows: [
        { label: 'Linia', value: item.linia },
        { label: 'Tydzień', value: item.tydzien },
        { label: 'Materiał', value: item.material },
        ...(item.barwnik ? [{ label: 'Barwnik', value: item.barwnik }] : []),
        ...(printedWeight ? [{ label: 'Waga szpuli', value: printedWeight }] : []),
        { label: 'Indeks', value: item.indeks_kat },
      ],
    });
    onClose();
  };

  // The network print does not need the label size (the backend reads it); the browser print does.
  const printDisabled = printState.phase === 'sending' || (!spool && !labelSize);

  return (
    <>
      {/* Modal overlay */}
      <div
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          padding: 16,
        }}
      >
        <div style={{
          background: '#fff', borderRadius: 12, padding: 'clamp(16px, 3vw, 32px)',
          maxWidth: 480, width: '100%',
          // Nie może być wyższy niż ekran — inaczej na tablecie ✕ i „Drukuj" wychodziły poza widok.
          maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20,
            // Nagłówek z przyciskiem zamknięcia przyklejony do góry — zawsze widoczny przy przewijaniu.
            position: 'sticky', top: 0, background: '#fff', zIndex: 1, paddingBottom: 8,
          }}>
            <h2 style={{ margin: 0, fontSize: 20, color: '#1e293b' }}>
              {t('largeWarehouse.qrModalTitle')}
            </h2>
            <button
              onClick={onClose}
              title={t('common.close')}
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer',
                fontSize: 22, lineHeight: 1, color: '#64748b', padding: 4,
              }}
            >
              ✕
            </button>
          </div>

          <div style={{
            background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8,
            padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#92400e',
          }}>
            {spool ? t('spoolLabel.info', { shift: shiftLabel }) : t('largeWarehouse.qrModalInfo')}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <canvas
              ref={canvasRef}
              style={{
                border: '1px solid #e2e8f0', borderRadius: 8,
                // Wyświetlany rozmiar mniejszy i responsywny (na tablecie/telefonie nie zasłania ekranu);
                // wewnętrzna rozdzielczość QR zostaje wysoka, więc kod pozostaje ostry i skanowalny.
                width: 'min(200px, 45vw)', height: 'min(200px, 45vw)', maxWidth: '100%',
              }}
            />
          </div>

          {/* Ręcznie wpisywana waga szpuli — docelowo uzupełniana automatycznie z wagi. */}
          {showWeight && (
            <div style={{
              border: '1px solid #e2e8f0', borderLeft: '3px solid #667eea', borderRadius: 8,
              padding: '12px 14px', marginBottom: 20,
            }}>
              <label
                htmlFor="qr-actual-weight"
                style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}
              >
                {t('largeWarehouse.qrModalWeightLabel')}
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  id="qr-actual-weight"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={weight}
                  disabled={!!saved}
                  onChange={(e) => {
                    const next = sanitizeWeight(e.target.value);
                    if (next !== null) setWeight(next);
                  }}
                  placeholder={t('largeWarehouse.qrModalWeightPlaceholder')}
                  style={{
                    flex: 1, padding: '8px 10px', border: '1px solid #cbd5e1',
                    borderRadius: 6, fontSize: 14,
                  }}
                />
                {!spool && (
                <button
                  onClick={saveWeight}
                  disabled={savingWeight || !weightDirty}
                  style={{
                    padding: '8px 16px', background: '#667eea', color: '#fff', border: 'none',
                    borderRadius: 6, fontSize: 14, fontWeight: 600,
                    cursor: (savingWeight || !weightDirty) ? 'default' : 'pointer',
                    opacity: (savingWeight || !weightDirty) ? 0.5 : 1,
                  }}
                >
                  {savingWeight ? t('largeWarehouse.saving') : t('largeWarehouse.save')}
                </button>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                {spool
                  ? t('spoolLabel.weightHelp', { shift: shiftLabel })
                    + (minWeightKg > 0 ? ` · ${t('spoolLabel.weightMin', { min: formatKgShort(minWeightKg) })}` : '')
                  : !weightDirty && savedWeight.trim() !== ''
                    ? t('largeWarehouse.qrModalWeightSaved')
                    : t('largeWarehouse.qrModalWeightHelp')}
              </div>
              {spool && canFinish && (
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                  <input
                    type="checkbox"
                    checked={finishItem}
                    disabled={!!saved}
                    onChange={(e) => setFinishItem(e.target.checked)}
                    style={{ width: 18, height: 18, accentColor: '#667eea' }}
                  />
                  {t('spoolLabel.finishItem')}
                </label>
              )}
              {spool && !canFinish && piecesAfter !== null && (
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 10 }}>
                  {t('spoolLabel.finishAfterPlan', { done: piecesAfter, plan: plannedPcs })}
                </div>
              )}
            </div>
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 20 }}>
            <tbody>
              {[
                ['Indeks', item.indeks_kat],
                ['Materiał', item.material],
                ['Średnica', item.srednica],
                ['Waga', item.waga],
                ['Szpula', item.szpula],
                ['Kolor', item.kolor],
                ['Barwnik', item.barwnik],
                ['Marka', item.marka],
                ['Linia', item.linia],
                ['Tydzień', item.tydzien],
              ].map(([label, value]) =>
                value ? (
                  <tr key={label as string} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '5px 8px', color: '#64748b', whiteSpace: 'nowrap', width: '40%' }}>{label}</td>
                    <td style={{ padding: '5px 8px', fontWeight: 600, color: '#1e293b' }}>{value}</td>
                  </tr>
                ) : null
              )}
            </tbody>
          </table>

          {printState.phase === 'sent' && (
            <div style={{ ...statusBox, background: '#d1fae5', borderColor: '#10b981', color: '#065f46' }}>
              {t('spoolLabel.sentTo', { printer: printState.printer })}
            </div>
          )}
          {printState.phase === 'failed' && saved && (
            <div style={{ ...statusBox, background: '#fee2e2', borderColor: '#ef4444', color: '#991b1b' }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                {t('spoolLabel.savedNotPrinted', { code: saved.label_code ?? String(saved.id) })}
              </div>
              <div style={{ marginBottom: 12 }}>{printState.message}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={retryNetworkPrint} style={{ ...actionButton, background: '#1e40af', color: '#fff' }}>
                  {t('spoolLabel.retry')}
                </button>
                <button onClick={fallbackBrowserPrint} disabled={!labelSize} style={{ ...actionButton, background: '#fff', color: '#1e40af', border: '2px solid #1e40af' }}>
                  {t('spoolLabel.browserPrint')}
                </button>
              </div>
            </div>
          )}
          {(printState.phase === 'form' || printState.phase === 'sending') && (
            <button
              onClick={handlePrint}
              disabled={printDisabled}
              style={{
                width: '100%', padding: '12px 0', background: '#1e40af', color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700,
                cursor: printDisabled ? 'not-allowed' : 'pointer', opacity: printDisabled ? 0.5 : 1,
              }}
            >
              {printState.phase === 'sending' ? t('spoolLabel.sending') : t('largeWarehouse.qrModalPrint')}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
